import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, Check, ArrowUpRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VitrineLayout from '../../components/layouts/VitrineLayout';
import {
  COPPER, COPPER_LIGHT, CREAM, DARK_BG, DARK_BG_2,
  DISPLAY_FF, ITALIC_FF, GRAIN_BG, GLASS_STYLE, COPPER_BTN_STYLE,
  CopperHalo, Eyebrow, Shimmer,
} from '../../components/vitrine/theme';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const HERO_MARBLE = "/hero-granipal.jpg";

const inputStyle = {
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid rgba(212,168,120,0.30)',
  borderRadius: '0',
  color: CREAM,
  fontFamily: 'Outfit, sans-serif',
  paddingLeft: '0',
  paddingRight: '0',
};

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/contact`, formData);
      setSuccess(true);
      toast.success('Message envoyé avec succès !');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VitrineLayout>

      {/* ============ HERO — full marble image + intro overlay ============ */}
      <section className="relative h-[28vh] min-h-[220px] lg:h-[32vh] overflow-hidden" data-testid="contact-hero">
        <img
          src={HERO_MARBLE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.78) saturate(0.95)' }}
        />
        <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(18,11,7,0.40) 0%, rgba(18,11,7,0.20) 35%, rgba(18,11,7,0.85) 100%)' }} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />
        <CopperHalo x="80%" y="50%" size={1000} opacity={0.14} />

        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-end pb-7 lg:pb-9">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <Eyebrow>Contact</Eyebrow>
            <h1
              style={{
                fontFamily: DISPLAY_FF,
                fontSize: 'clamp(1.9rem, 4.5vw, 3.6rem)',
                fontWeight: 400,
                color: CREAM,
                letterSpacing: '0.01em',
                lineHeight: 1,
                textShadow: '0 2px 14px rgba(0,0,0,0.45)',
              }}
            >
              Parlons de{' '}
              <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: ITALIC_FF }}>
                votre projet
              </em>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ============ Editorial split: 12-col grid with info + form + map ============ */}
      <section className="relative overflow-hidden" style={{ background: DARK_BG }}>
        <CopperHalo x="10%" y="30%" size={1100} opacity={0.12} />
        <CopperHalo x="90%" y="80%" size={900} opacity={0.10} />
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 15% 15%, rgba(184,137,90,0.08) 0%, transparent 50%), radial-gradient(ellipse at 85% 85%, rgba(212,168,120,0.06) 0%, transparent 50%)' }} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-10">
          {/* Intro phrase */}
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-2xl mb-8 lg:mb-10 text-base lg:text-lg font-light leading-relaxed"
            style={{ color: 'rgba(245,230,208,0.78)', fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}
          >
            « Une visite à l'atelier, un devis, une question technique — nos marbriers vous
            accueillent et vous accompagnent à chaque étape. »
          </motion.p>

          {/* 12-col grid */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* LEFT — info column (4 cols), each info on its own row, large typography */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04, delayChildren: 0.0 } } }}
              className="lg:col-span-4 flex flex-col gap-7 lg:gap-8"
            >
              {[
                { icon: MapPin, label: 'Adresse',   primary: '6 Rue de la porte écluse', secondary: '91270 Vigneux-sur-Seine' },
                { icon: Phone,  label: 'Téléphone', primary: '01 69 83 04 79',           secondary: '06 13 09 36 39' },
                { icon: Mail,   label: 'Email',     primary: 'granipal@orange.fr',       secondary: 'Réponse sous 24 h' },
                { icon: Clock,  label: 'Horaires',  primary: 'Lun – Jeu : 8h – 17h',     secondary: 'Vendredi : 8h – 16h · Samedi : sur rendez-vous' },
              ].map((info) => (
                <motion.div
                  key={info.label}
                  variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } } }}
                  className="group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <info.icon className="w-3.5 h-3.5 shrink-0" style={{ color: COPPER_LIGHT }} />
                    <span className="text-[10px] uppercase tracking-[0.42em]" style={{ color: COPPER_LIGHT }}>{info.label}</span>
                  </div>
                  <p
                    style={{
                      fontFamily: DISPLAY_FF,
                      fontSize: 'clamp(1.2rem, 1.6vw, 1.45rem)',
                      fontWeight: 400,
                      color: CREAM,
                      letterSpacing: '0.005em',
                      lineHeight: 1.25,
                    }}
                  >
                    {info.primary}
                  </p>
                  <p className="mt-1 text-[13px] font-light" style={{ color: 'rgba(245,230,208,0.55)' }}>
                    {info.secondary}
                  </p>
                  <span aria-hidden className="block h-px mt-5 transition-all duration-500 group-hover:w-16" style={{ background: COPPER_LIGHT, width: '32px', opacity: 0.5 }} />
                </motion.div>
              ))}
            </motion.div>

            {/* RIGHT — form (8 cols) wrapped in editorial frame */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-8"
            >
              <div
                className="relative p-8 lg:p-12 overflow-hidden"
                style={{
                  ...GLASS_STYLE,
                  borderRadius: '0px',
                }}
              >
                {/* Decorative corner accents */}
                <span aria-hidden className="absolute top-4 left-4 w-10 h-px" style={{ background: COPPER_LIGHT, opacity: 0.45 }} />
                <span aria-hidden className="absolute top-4 left-4 w-px h-10" style={{ background: COPPER_LIGHT, opacity: 0.45 }} />
                <span aria-hidden className="absolute bottom-4 right-4 w-10 h-px" style={{ background: COPPER_LIGHT, opacity: 0.45 }} />
                <span aria-hidden className="absolute bottom-4 right-4 w-px h-10" style={{ background: COPPER_LIGHT, opacity: 0.45 }} />

                <Eyebrow>Formulaire</Eyebrow>
                <h2
                  className="mb-10"
                  style={{
                    fontFamily: DISPLAY_FF,
                    fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
                    fontWeight: 400,
                    color: CREAM,
                    letterSpacing: '0.01em',
                    lineHeight: 1.15,
                  }}
                >
                  Écrivez-nous{' '}
                  <em style={{ fontFamily: ITALIC_FF, fontStyle: 'italic', color: COPPER_LIGHT }}>quelques lignes</em>
                </h2>

                {success ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full" style={{ background: 'rgba(184,137,90,0.18)', border: '1px solid rgba(212,168,120,0.55)' }}>
                      <Check className="w-7 h-7" style={{ color: COPPER_LIGHT }} />
                    </div>
                    <h3 className="mb-2" style={{ fontFamily: DISPLAY_FF, fontSize: '1.5rem', color: CREAM, letterSpacing: '0.02em' }}>Message envoyé</h3>
                    <p className="text-[13px] font-light" style={{ color: 'rgba(245,230,208,0.65)' }}>Nous vous répondrons dans les meilleurs délais.</p>
                    <button onClick={() => setSuccess(false)} className="mt-6 px-7 py-2.5 text-[11px] uppercase tracking-[0.3em]" style={{ color: COPPER_LIGHT, border: '1px solid rgba(212,168,120,0.45)' }}>
                      Envoyer un autre message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-7">
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                      <Field label="Nom complet *">
                        <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Votre nom" className="w-full py-3 text-sm outline-none placeholder:text-white/30 focus:border-[#D4A878] transition" style={inputStyle} data-testid="contact-name" />
                      </Field>
                      <Field label="Email *">
                        <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required placeholder="votre@email.com" className="w-full py-3 text-sm outline-none placeholder:text-white/30 focus:border-[#D4A878] transition" style={inputStyle} data-testid="contact-email" />
                      </Field>
                      <Field label="Téléphone">
                        <input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="06 00 00 00 00" className="w-full py-3 text-sm outline-none placeholder:text-white/30 focus:border-[#D4A878] transition" style={inputStyle} data-testid="contact-phone" />
                      </Field>
                      <Field label="Sujet *">
                        <input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required placeholder="Sujet" className="w-full py-3 text-sm outline-none placeholder:text-white/30 focus:border-[#D4A878] transition" style={inputStyle} data-testid="contact-subject" />
                      </Field>
                    </div>
                    <Field label="Message *">
                      <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows={5} placeholder="Décrivez votre projet…" className="w-full py-3 text-sm outline-none placeholder:text-white/30 focus:border-[#D4A878] transition resize-none" style={inputStyle} data-testid="contact-message" />
                    </Field>

                    <button
                      type="submit"
                      disabled={loading}
                      className="group relative inline-flex items-center justify-center gap-3 px-10 py-4 uppercase text-[11px] tracking-[0.3em] transition-all duration-500 overflow-hidden disabled:opacity-50 mt-4"
                      style={{ ...COPPER_BTN_STYLE, borderRadius: '2px' }}
                      data-testid="contact-submit"
                    >
                      <Shimmer />
                      {loading ? (
                        <span className="relative w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="relative w-4 h-4" />
                          <span className="relative">Envoyer le message</span>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ MAP — full-width with floating address card ============ */}
      <section className="relative overflow-hidden" style={{ background: DARK_BG_2 }} data-testid="contact-map">
        <div className="relative h-[500px] lg:h-[600px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2630.8876744!2d2.4108!3d48.7025!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDQyJzA5LjAiTiAywrAyNCczOS4wIkU!5e0!3m2!1sfr!2sfr!4v1640000000000!5m2!1sfr!2sfr"
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'brightness(0.55) saturate(0.6) contrast(115%) hue-rotate(8deg)' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Localisation Granipal"
          />
          {/* Custom marker overlay — small, elegant copper-cuivré pin with a discrete red accent. */}
          <div aria-hidden className="absolute top-1/2 left-1/2 pointer-events-none z-[2]"
               style={{ transform: 'translate(-50%, calc(-50% - 8px))' }}>
            <div className="relative flex flex-col items-center">
              {/* Outer ring — copper-cuivré, premium */}
              <div
                className="relative flex items-center justify-center"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: 'rgba(20,11,7,0.85)',
                  border: '1.5px solid rgba(212,168,120,0.9)',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.55), 0 0 0 4px rgba(184,137,90,0.18), 0 0 12px rgba(212,168,120,0.35)',
                }}
              >
                {/* Inner red dot — the locator */}
                <span
                  aria-hidden
                  className="block"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#E11D2C',
                    boxShadow: '0 0 6px rgba(225,29,44,0.85)',
                  }}
                />
              </div>
              {/* Thin pin tail */}
              <span
                aria-hidden
                className="block"
                style={{
                  width: 1.5,
                  height: 14,
                  background: 'linear-gradient(180deg, rgba(212,168,120,0.9) 0%, rgba(212,168,120,0) 100%)',
                  marginTop: -2,
                }}
              />
              {/* Pulse halo */}
              <span
                aria-hidden
                className="absolute top-[2px] block rounded-full"
                style={{
                  width: 22,
                  height: 22,
                  background: 'transparent',
                  border: '1.5px solid rgba(212,168,120,0.7)',
                  animation: 'granipal-pin-pulse 2.6s ease-out infinite',
                }}
              />
            </div>
            <style>{`
              @keyframes granipal-pin-pulse {
                0%   { transform: scale(0.6); opacity: 0.8; }
                70%  { transform: scale(2.8); opacity: 0; }
                100% { transform: scale(2.8); opacity: 0; }
              }
            `}</style>
          </div>
          {/* Subtle copper inner ring to keep the map framed */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              boxShadow: 'inset 0 0 0 1px rgba(212,168,120,0.35), inset 0 0 60px 0 rgba(18,11,7,0.45)',
            }}
          />
          {/* Top gradient blend */}
          <div aria-hidden className="absolute inset-x-0 top-0 h-32 pointer-events-none" style={{ background: `linear-gradient(180deg, ${DARK_BG_2} 0%, transparent 100%)` }} />

          {/* Floating address card — bottom left */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, amount: 0.3 }}
            className="absolute bottom-8 left-6 lg:bottom-12 lg:left-12 max-w-md px-7 py-6 lg:px-9 lg:py-8"
            style={{
              ...GLASS_STYLE,
              background: 'linear-gradient(160deg, rgba(245,230,208,0.10) 0%, rgba(184,137,90,0.06) 60%, rgba(20,11,7,0.55) 100%)',
              borderRadius: '0px',
              backdropFilter: 'blur(20px) saturate(160%)',
              WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            }}
          >
            <Eyebrow>Nous rendre visite</Eyebrow>
            <h3
              className="mb-4"
              style={{
                fontFamily: DISPLAY_FF,
                fontSize: '1.5rem',
                fontWeight: 400,
                color: CREAM,
                letterSpacing: '0.01em',
                lineHeight: 1.2,
              }}
            >
              Atelier Granipal,<br /><em style={{ fontFamily: ITALIC_FF, fontStyle: 'italic', color: COPPER_LIGHT }}>Vigneux-sur-Seine</em>
            </h3>
            <p className="text-[13px] font-light leading-relaxed mb-5" style={{ color: 'rgba(245,230,208,0.72)' }}>
              Parking sur place. À 5 minutes à pied de la gare de Vigneux-sur-Seine (RER&nbsp;D).
              Showroom ouvert du lundi au vendredi, samedi sur rendez-vous.
            </p>
            <a
              href="https://maps.google.com/?q=6+Rue+de+la+porte+écluse+91270+Vigneux-sur-Seine"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] transition-colors duration-500"
              style={{ color: COPPER_LIGHT }}
              onMouseEnter={(e) => { e.currentTarget.style.color = CREAM; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = COPPER_LIGHT; }}
              data-testid="open-map-link"
            >
              Itinéraire
              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-500" />
            </a>
          </motion.div>
        </div>
      </section>
    </VitrineLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.32em] mb-1 block" style={{ color: 'rgba(245,230,208,0.65)' }}>{label}</span>
      {children}
    </label>
  );
}
